import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiErr } from "../../lib/api";
import { formatINR, categoryMeta } from "../../lib/format";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { InfoTip, GLOSSARY } from "../../components/app/InfoTip";
import AddCommitmentDialog from "../../components/app/AddCommitmentDialog";
import ShareSnapshot from "../../components/app/ShareSnapshot";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, CalendarDays, Clock, AlertCircle, AlertTriangle,
  CheckCircle2, PauseCircle, XCircle, RefreshCw, Plus, Pencil, Check,
} from "lucide-react";

const STATUS = {
  upcoming: { label: "Upcoming", icon: Clock, cls: "bg-secondary text-slatey", dot: "bg-navy" },
  due_today: { label: "Due today", icon: AlertCircle, cls: "bg-calm-pendingbg text-calm-pending", dot: "bg-calm-pending" },
  overdue: { label: "Overdue", icon: AlertTriangle, cls: "bg-calm-alertbg text-calm-alert", dot: "bg-calm-alert" },
  paid: { label: "Paid", icon: CheckCircle2, cls: "bg-calm-successbg text-calm-success", dot: "bg-calm-success" },
  paused: { label: "Paused", icon: PauseCircle, cls: "bg-secondary text-muted-foreground", dot: "bg-muted-foreground" },
  cancelled: { label: "Cancelled", icon: XCircle, cls: "bg-secondary text-muted-foreground", dot: "bg-muted-foreground" },
};
const ymKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.upcoming; const Icon = s.icon;
  return <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${s.cls}`} data-testid={`status-${status}`}><Icon size={11} />{s.label}</span>;
}

function dayMeta(items) {
  if (!items || !items.length) return null;
  const has = (s) => items.some((x) => x.status === s);
  const status = has("overdue") ? "overdue" : has("due_today") ? "due_today"
    : items.every((x) => x.status === "paid") ? "paid" : "upcoming";
  return { status, count: items.length };
}

// Minimal, accessible day marker. State is encoded by SHAPE (dot / ring / check / count), not colour alone.
function DayMarker({ meta, isToday }) {
  if (!meta) return null;
  const { status, count } = meta;
  const tone = {
    overdue: "bg-calm-alert text-white", due_today: "bg-calm-pending text-white",
    upcoming: "bg-navy text-white", paid: "bg-calm-success text-white",
  }[status];
  if (count > 1) {
    return <span data-testid="day-count-badge"
      className={`mt-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold leading-4 flex items-center justify-center ${isToday ? "bg-white text-navy" : tone}`}>{count}</span>;
  }
  if (status === "paid") return <Check size={12} strokeWidth={3} className={`mt-0.5 ${isToday ? "text-mint" : "text-calm-success"}`} data-testid="day-marker-paid" />;
  if (status === "overdue") return <span data-testid="day-marker-overdue" className={`mt-0.5 h-2 w-2 rounded-full border-2 ${isToday ? "border-mint" : "border-calm-alert"}`} />;
  if (status === "due_today") return <span data-testid="day-marker-due" className={`mt-0.5 h-1.5 w-1.5 rounded-full ${isToday ? "bg-mint ring-2 ring-white/50" : "bg-calm-pending ring-2 ring-calm-pending/30"}`} />;
  return <span data-testid="day-marker-upcoming" className={`mt-0.5 h-1.5 w-1.5 rounded-full ${isToday ? "bg-mint" : "bg-navy"}`} />;
}

export default function CalendarPage() {
  const ctx = useOutletContext() || {};
  const [view, setView] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => iso(new Date()));
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const today = new Date();
  const todayIso = iso(today);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cal, sum] = await Promise.all([
        api.get(`/calendar?month=${ymKey(view)}`),
        api.get("/upcoming-summary"),
      ]);
      setData(cal.data); setSummary(sum.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => data?.days || {}, [data]);
  const monthLabel = view.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const firstWeekday = new Date(view.getFullYear(), view.getMonth(), 1).getDay();
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedItems = days[selected] || [];
  const selectedTotal = selectedItems.reduce((s, c) => s + (c.amount || 0), 0);
  const selDateObj = selected ? (() => { const [y, m, dd] = selected.split("-").map(Number); return new Date(y, m - 1, dd); })() : null;

  const goToday = () => { const d = new Date(); setView(new Date(d.getFullYear(), d.getMonth(), 1)); setSelected(iso(d)); };
  const shift = (n) => setView((v) => new Date(v.getFullYear(), v.getMonth() + n, 1));

  const dueText = (item) => {
    if (item.status === "paid") return "Paid";
    const [yy, mm, dd] = item.date.split("-").map(Number);
    const d0 = new Date(); d0.setHours(0, 0, 0, 0);
    const dt = new Date(yy, mm - 1, dd); dt.setHours(0, 0, 0, 0);
    const n = Math.round((dt - d0) / 86400000);
    if (n === 0) return "Due today";
    if (n > 0) return `Due in ${n} day${n === 1 ? "" : "s"}`;
    return `${-n} day${n === -1 ? "" : "s"} overdue`;
  };

  const weekAllPaid = useMemo(() => {
    const now = new Date();
    if (view.getFullYear() !== now.getFullYear() || view.getMonth() !== now.getMonth()) return false;
    const start = new Date(now); start.setDate(now.getDate() - now.getDay()); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    let due = 0, paid = 0;
    Object.entries(days).forEach(([k, arr]) => {
      const [y, m, dd] = k.split("-").map(Number); const dt = new Date(y, m - 1, dd);
      if (dt >= start && dt <= end) {
        arr.forEach((it) => {
          if (it.status === "paid") paid++;
          else if (["upcoming", "due_today", "overdue"].includes(it.status)) due++;
        });
      }
    });
    return paid > 0 && due === 0;
  }, [days, view]);

  const refreshAll = () => { load(); ctx.triggerRefresh && ctx.triggerRefresh(); };

  const markPaid = async (item) => {
    try { await api.post(`/commitments/${item.id}/mark-paid`, { date: item.date }); toast.success(`Marked ${item.name} as paid.`); refreshAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const markUnpaid = async (item) => {
    try { await api.post(`/commitments/${item.id}/mark-unpaid`, { date: item.date }); toast.success(`Marked ${item.name} as unpaid.`); refreshAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openEdit = async (item) => {
    try { const { data: full } = await api.get("/commitments"); const c = full.find((x) => x.id === item.id); if (c) { setEditItem(c); } }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addOnSelected = () => { setAddDate(selected); setAddOpen(true); };

  if (loading && !data) return <div className="space-y-3"><Skeleton className="h-9 w-40 rounded-full" /><Skeleton className="h-64 rounded-2xl" /></div>;

  const t = data?.totals || { committed: 0, paid: 0, remaining: 0 };

  return (
    <div data-testid="calendar-page">
      <div className="flex items-center gap-2 mb-4">
        <CalendarDays size={22} className="text-navy" strokeWidth={1.5} />
        <h1 className="font-heading text-2xl font-medium text-ink">Calendar</h1>
      </div>

      {/* Upcoming summaries */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card className="rounded-2xl border-border shadow-card-sm p-4" data-testid="summary-next-7">
          <p className="text-xs text-slatey">Next 7 days</p>
          <p className="font-heading text-xl text-navy tnum mt-1">{formatINR(summary?.next_7?.total || 0)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{summary?.next_7?.count || 0} payment{(summary?.next_7?.count || 0) === 1 ? "" : "s"}</p>
        </Card>
        <Card className="rounded-2xl border-border shadow-card-sm p-4" data-testid="summary-next-30">
          <p className="text-xs text-slatey">Next 30 days</p>
          <p className="font-heading text-xl text-navy tnum mt-1">{formatINR(summary?.next_30?.total || 0)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{summary?.next_30?.count || 0} payment{(summary?.next_30?.count || 0) === 1 ? "" : "s"}</p>
        </Card>
      </div>

      {weekAllPaid && (
        <Card className="rounded-2xl border-calm-success/30 bg-calm-successbg shadow-card-sm p-4 mb-4 flex items-start gap-2.5" data-testid="week-caught-up">
          <CheckCircle2 size={18} className="text-calm-success mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ink">You're all caught up</p>
            <p className="text-xs text-slatey mt-0.5">Every payment due this week is marked paid. Enjoy the calm — nothing else needs your attention right now.</p>
            <div className="mt-2"><ShareSnapshot variant="streak" /></div>
          </div>
        </Card>
      )}

      {!weekAllPaid && summary?.next_7?.count >= 3 && (
        <Card className="rounded-2xl border-mint/30 bg-mint-light/40 shadow-card-sm p-4 mb-4 flex items-start gap-2.5" data-testid="heavy-week-tip">
          <AlertCircle size={16} className="text-mint mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-ink">Heads up</p>
            <p className="text-xs text-slatey mt-0.5">You have {formatINR(summary.next_7.total)} recorded as due over the next 7 days across {summary.next_7.count} payments.</p>
          </div>
        </Card>
      )}

      <Card className="rounded-2xl border-border shadow-card-sm p-5" data-testid="calendar-card">
        {/* Month header + nav */}
        <div className="flex items-center justify-between mb-1">
          <button onClick={() => shift(-1)} aria-label="Previous month" data-testid="cal-prev" className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-secondary text-slatey"><ChevronLeft size={18} /></button>
          <div className="text-center">
            <p className="font-heading text-lg text-ink" data-testid="cal-month-label">{monthLabel}</p>
          </div>
          <button onClick={() => shift(1)} aria-label="Next month" data-testid="cal-next" className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-secondary text-slatey"><ChevronRight size={18} /></button>
        </div>
        <div className="flex justify-center mb-4">
          <Button size="sm" variant="ghost" onClick={goToday} className="rounded-full text-mint h-7 text-xs" data-testid="cal-today-btn">Today</Button>
        </div>

        {/* Monthly overview */}
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div className="rounded-xl bg-secondary/60 py-2.5">
            <p className="text-[11px] text-slatey flex items-center justify-center gap-1">Committed <InfoTip {...GLOSSARY.committed} className="h-4 w-4" /></p>
            <p className="font-medium text-ink tnum text-sm mt-0.5" data-testid="month-committed">{formatINR(t.committed)}</p>
          </div>
          <div className="rounded-xl bg-secondary/60 py-2.5">
            <p className="text-[11px] text-slatey">Paid</p>
            <p className="font-medium text-calm-success tnum text-sm mt-0.5" data-testid="month-paid">{formatINR(t.paid)}</p>
          </div>
          <div className="rounded-xl bg-secondary/60 py-2.5">
            <p className="text-[11px] text-slatey">Remaining</p>
            <p className="font-medium text-navy tnum text-sm mt-0.5" data-testid="month-remaining">{formatINR(t.remaining)}</p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-7 gap-1 text-center">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="text-[11px] text-muted-foreground py-1 font-medium">{d}</div>)}
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const dObj = new Date(view.getFullYear(), view.getMonth(), d);
            const dIso = iso(dObj);
            const items = days[dIso] || [];
            const isToday = dIso === todayIso;
            const isSel = dIso === selected;
            const meta = dayMeta(items);
            return (
              <button key={i} onClick={() => setSelected(dIso)} data-testid={`cal-day-${d}`}
                aria-label={`${d} ${monthLabel}${items.length ? `, ${items.length} commitment${items.length > 1 ? "s" : ""}` : ""}${isSel ? ", selected" : ""}${isToday ? ", today" : ""}`}
                aria-pressed={isSel}
                className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm relative transition-colors min-h-[40px]
                  ${isSel && !isToday ? "ring-2 ring-mint ring-offset-1" : ""} ${isSel && isToday ? "ring-2 ring-mint ring-offset-1" : ""} ${isToday ? "bg-navy text-white font-semibold" : "hover:bg-secondary text-ink"}`}>
                <span className="tnum">{d}</span>
                <DayMarker meta={meta} isToday={isToday} />
              </button>
            );
          })}
        </div>

        {/* Legend — shape/icon distinguishes states (not colour alone) */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 pt-3 border-t border-border/60 text-[11px] text-slatey" data-testid="calendar-legend">
          <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-navy" />Upcoming</span>
          <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-calm-pending ring-2 ring-calm-pending/30" />Due today</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full border-2 border-calm-alert" />Overdue</span>
          <span className="flex items-center gap-1.5"><Check size={12} strokeWidth={3} className="text-calm-success" />Paid</span>
          <span className="flex items-center gap-1.5"><span className="min-w-[16px] h-4 px-1 rounded-full bg-navy text-white text-[10px] font-semibold leading-4 flex items-center justify-center">2</span>Multiple</span>
        </div>
      </Card>

      {/* Selected day panel */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5 mt-4" data-testid="day-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-heading text-lg text-ink" data-testid="day-panel-date">{selDateObj ? selDateObj.toLocaleDateString("en-IN", { day: "numeric", month: "long" }) : "—"}</p>
            {selectedItems.length > 0 && (
              <p className="text-xs text-slatey mt-0.5" data-testid="day-panel-summary">
                {selectedItems.length} commitment{selectedItems.length > 1 ? "s" : ""} · <span className="font-medium text-ink tnum">{formatINR(selectedTotal)}</span>
              </p>
            )}
          </div>
          <Button size="sm" onClick={addOnSelected} className="rounded-full bg-mint text-white hover:bg-mint/90 h-9" data-testid="day-add-btn"><Plus size={15} className="mr-1" />Add</Button>
        </div>
        {selectedItems.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="day-empty">Nothing committed for this day.</p>
        ) : (
          <div className="divide-y divide-border/60 max-h-[320px] overflow-y-auto -mx-1 px-1" data-testid="day-items">
            {selectedItems.map((item, idx) => {
              const cm = categoryMeta(item.category);
              const CIcon = cm.icon;
              return (
                <button key={idx} onClick={() => openEdit(item)} data-testid={`day-item-${item.id}`}
                  className="w-full py-3 flex items-center gap-3 text-left hover:bg-secondary/50 rounded-xl transition-colors">
                  <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0"><CIcon size={16} strokeWidth={1.5} className="text-navy" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-ink truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {cm.label}{item.payment_source ? ` · ${item.payment_source}` : ""} · {dueText(item)}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <StatusChip status={item.status} />
                      {item.autopay && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-secondary text-slatey"><RefreshCw size={10} />Auto-pay</span>}
                    </div>
                  </div>
                  <p className="font-semibold text-ink tnum shrink-0">{formatINR(item.amount)}</p>
                </button>
              );
            })}
          </div>
        )}
        {selectedItems.length > 0 && (
          <div className="mt-3 space-y-2">
            {selectedItems.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                {item.status === "paid" ? (
                  <Button size="sm" variant="secondary" onClick={() => markUnpaid(item)} className="rounded-full flex-1 h-8 text-xs" data-testid={`unpaid-${item.id}`}>Mark unpaid</Button>
                ) : (
                  <Button size="sm" onClick={() => markPaid(item)} className="rounded-full flex-1 h-8 text-xs bg-navy text-white hover:bg-navy-light" data-testid={`paid-${item.id}`}><CheckCircle2 size={13} className="mr-1" />Mark {item.name} paid</Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => openEdit(item)} className="rounded-full h-8 text-xs text-slatey" data-testid={`edit-${item.id}`}><Pencil size={13} className="mr-1" />Edit</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <AddCommitmentDialog open={addOpen} onOpenChange={setAddOpen} defaultDate={addDate} onSaved={refreshAll} />
      <AddCommitmentDialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)} existing={editItem} onSaved={() => { setEditItem(null); refreshAll(); }} />
    </div>
  );
}
